import type { OctreeGeometryNode } from '@/plugins/pointCloud/core/OctreeGeometry'

class ResidentNodeLRUItem {
  previous: ResidentNodeLRUItem | null = null
  next: ResidentNodeLRUItem | null = null

  constructor(readonly node: OctreeGeometryNode) {}
}

/** 不受当前节点后续移除影响的最旧端遍历游标。 */
export interface ResidentNodeLRUCursor {
  next(): OctreeGeometryNode | null
}

/**
 * 仅维护驻留节点最近使用顺序的索引。
 *
 * 不读取预算、帧号或可见性，也不负责释放节点资源。
 */
export class ResidentNodeLRU {
  private first: ResidentNodeLRUItem | null = null
  private last: ResidentNodeLRUItem | null = null
  private readonly items = new Map<OctreeGeometryNode, ResidentNodeLRUItem>()

  get size(): number {
    return this.items.size
  }

  /** 将尚未登记的节点加入最近使用端；重复调用为安全空操作。 */
  add(node: OctreeGeometryNode): void {
    if (this.items.has(node)) {
      return
    }

    const item = new ResidentNodeLRUItem(node)
    item.previous = this.last
    if (this.last) {
      this.last.next = item
    } else {
      this.first = item
    }
    this.last = item
    this.items.set(node, item)
  }

  /** 将节点移动到最近使用端；未登记节点会先加入，重复调用保持幂等。 */
  touch(node: OctreeGeometryNode): void {
    const item = this.items.get(node)
    if (!item) {
      this.add(node)
      return
    }
    if (item === this.last) {
      return
    }

    if (item.previous) {
      item.previous.next = item.next
    } else {
      this.first = item.next
    }
    if (item.next) {
      item.next.previous = item.previous
    }

    item.previous = this.last
    item.next = null
    this.last!.next = item
    this.last = item
  }

  /** 从顺序索引移除节点；节点不存在时为安全空操作。 */
  remove(node: OctreeGeometryNode): void {
    const item = this.items.get(node)
    if (!item) {
      return
    }

    if (item.previous) {
      item.previous.next = item.next
    } else {
      this.first = item.next
    }
    if (item.next) {
      item.next.previous = item.previous
    } else {
      this.last = item.previous
    }

    this.items.delete(node)
  }

  /** 创建从最旧端向最近端推进的游标，不构造全量节点数组。 */
  createOldestFirstCursor(): ResidentNodeLRUCursor {
    let item = this.first
    return {
      next: () => {
        if (!item) {
          return null
        }
        const current = item
        item = current.next
        return current.node
      },
    }
  }

  /** 返回从最旧到最新的节点快照，不改变顺序。 */
  oldestFirst(): OctreeGeometryNode[] {
    const nodes: OctreeGeometryNode[] = []
    let item = this.first
    while (item) {
      nodes.push(item.node)
      item = item.next
    }
    return nodes
  }

  /** 清空顺序索引，不释放节点资源。 */
  clear(): void {
    this.items.clear()
    this.first = null
    this.last = null
  }
}
